import React, { useState } from 'react';

// Minimal test component to isolate file upload issue
export const FileUploadTest: React.FC = () => {
  const [status, setStatus] = useState('Ready to test');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toISOString().split('T')[1];
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
    console.log(`[FileUploadTest] ${message}`);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addLog('handleFileSelect called');
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (file) {
      addLog(`File selected: ${file.name} (${file.size} bytes)`);
      setStatus(`File selected: ${file.name}`);
    } else {
      addLog('No file selected');
    }
    
    // Reset input to allow re-selecting same file
    e.target.value = '';
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    addLog('Form submitted - THIS SHOULD NOT HAPPEN');
    e.preventDefault();
    return false;
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">File Upload Test Component</h2>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-6">
        <p className="text-sm">
          This is a minimal test component to isolate the file upload issue.
          If this works without page refresh, the issue is in the parent components.
        </p>
      </div>

      <div className="space-y-6">
        {/* Test 1: Basic input without form */}
        <div className="border rounded p-4">
          <h3 className="font-semibold mb-2">Test 1: Input without form</h3>
          <input
            type="file"
            onChange={handleFileSelect}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {/* Test 2: Input inside form (with prevention) */}
        <div className="border rounded p-4">
          <h3 className="font-semibold mb-2">Test 2: Input inside form (prevented)</h3>
          <form onSubmit={handleFormSubmit}>
            <input
              type="file"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
            />
          </form>
        </div>

        {/* Test 3: Hidden input with click trigger */}
        <div className="border rounded p-4">
          <h3 className="font-semibold mb-2">Test 3: Hidden input with button</h3>
          <button
            onClick={(e) => {
              e.preventDefault();
              addLog('Button clicked');
              document.getElementById('test-file-input')?.click();
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            Choose File
          </button>
          <input
            id="test-file-input"
            type="file"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Status Display */}
        <div className="border rounded p-4 bg-gray-50">
          <h3 className="font-semibold mb-2">Status</h3>
          <p className="text-green-600 font-mono">{status}</p>
        </div>

        {/* Logs Display */}
        <div className="border rounded p-4 bg-gray-900 text-white">
          <h3 className="font-semibold mb-2">Event Logs</h3>
          <div className="font-mono text-xs space-y-1 max-h-40 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-400">No events yet...</p>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="text-green-400">{log}</div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 text-sm text-gray-600">
        <p className="font-semibold">Instructions:</p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>Try each test above</li>
          <li>If the page refreshes, note which test caused it</li>
          <li>Check the browser console for errors</li>
          <li>Check the Event Logs above for the last event before refresh</li>
        </ol>
      </div>
    </div>
  );
}; 